import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import ShowModal from '../components/ShowModal';
import ShareModal from '../components/ShareModal';
import ShowReviewsModal from '../components/ShowReviewsModal';
import ShowFinderBanner from '../components/ShowFinderBanner';
import { ServiceLabel } from '../contexts/StreamingServicesContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { usePageView } from '../hooks/usePageView';
import { Eye, Clock, Check, Bookmark, UserPlus, Calendar, Tv, History } from 'lucide-react';
import dashboardStyles from './Dashboard.module.css';
import showStyles from './MyShows.module.css';
import SEO from '../components/SEO';
import TrendingPromoSection from '../components/TrendingPromoSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  usePageView('dashboard');
  const [stats, setStats] = useState({
    watching: 0,
    waiting: 0,
    completed: 0,
    wantToWatch: 0
  });
  const [recentShows, setRecentShows] = useState([]);
  const [comingEpisodes, setComingEpisodes] = useState([]);
  const [recentEpisodes, setRecentEpisodes] = useState([]);
  const [showStatistics, setShowStatistics] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedShowForReviews, setSelectedShowForReviews] = useState(null);
  const navigate = useNavigate();

  const handleShowClick = (show) => {
    // Transform show data to match the format expected by ShowReviewsModal
    const showData = {
      show_title: show.title,
      show_image_url: show.image_url,
      backdrop_url: show.backdrop_url,
      streaming_service: show.streaming_service,
      custom_service: show.custom_service,
      overview: show.overview,
      // Episode tracking data
      show_status: show.show_status,
      in_production: show.in_production,
      next_episode: show.next_episode,
      last_episode: show.last_episode,
      release_date: show.release_date,
      genres: show.genres
    };
    setSelectedShowForReviews(showData);
  };

  useEffect(() => {
    loadDashboardData();
    // Refresh when shows are added/changed elsewhere (e.g. Show Finder wizard)
    const handler = () => loadDashboardData();
    window.addEventListener('baadtv:shows-changed', handler);
    // Also refresh when the tab regains focus (covers add-from-another-tab)
    const onFocus = () => loadDashboardData();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('baadtv:shows-changed', handler);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/shows`, { withCredentials: true });
      const shows = response.data;

      setStats({
        watching: shows.filter(s => s.status === 'Currently Watching').length,
        waiting: shows.filter(s => s.status === 'Waiting for Next Season').length,
        completed: shows.filter(s => s.status === 'Completed').length,
        wantToWatch: shows.filter(s => s.status === 'Want to Watch').length
      });

      const recent = shows.slice(0, 5);
      setRecentShows(recent);

      // Filter shows with upcoming episodes (based on local release date)
      // Episodes stay on the list until the end of their release day
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(todayStart);
      todayEnd.setDate(todayEnd.getDate() + 1); // End of today (start of tomorrow)
      const nextWeek = new Date(todayStart);
      nextWeek.setDate(nextWeek.getDate() + 8); // 7 days from today end

      const upcoming = shows
        .filter(show => {
          if (!show.next_episode || !show.next_episode.air_date) return false;
          // Calculate local release time (midnight PT at end of broadcast day)
          const airDateStr = show.next_episode.air_date;
          const releaseTimePT = new Date(`${airDateStr}T00:00:00-08:00`);
          releaseTimePT.setDate(releaseTimePT.getDate() + 1); // End of broadcast day = midnight PT
          
          // Get just the date portion in local timezone for comparison
          const releaseDateLocal = new Date(releaseTimePT.getFullYear(), releaseTimePT.getMonth(), releaseTimePT.getDate());
          
          // Include if release date is today or in the future (up to next week)
          // Use < todayEnd instead of >= todayStart to keep today's episodes until midnight
          return releaseDateLocal < nextWeek && releaseDateLocal >= todayStart;
        })
        .map(show => {
          // Calculate local release time
          const airDateStr = show.next_episode.air_date;
          const releaseTime = new Date(`${airDateStr}T00:00:00-08:00`);
          releaseTime.setDate(releaseTime.getDate() + 1); // End of broadcast day
          
          // Get just the date portion in local timezone
          const releaseDateLocal = new Date(releaseTime.getFullYear(), releaseTime.getMonth(), releaseTime.getDate());
          
          // Calculate days until release (using calendar days)
          const dayDiff = Math.round((releaseDateLocal - todayStart) / (1000 * 60 * 60 * 24));
          
          return {
            ...show,
            daysUntil: dayDiff,
            localReleaseTime: releaseTime,
            usBroadcastDate: show.next_episode.air_date
          };
        })
        .sort((a, b) => a.localReleaseTime - b.localReleaseTime);

      setComingEpisodes(upcoming);

      // Filter shows with episodes from the past week (last_episode)
      const oneWeekAgo = new Date(todayStart);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const recentlyAired = shows
        .filter(show => {
          if (!show.last_episode || !show.last_episode.air_date) return false;
          // Only show episodes from "Currently Watching" shows
          if (show.status !== 'Currently Watching') return false;
          
          const airDateStr = show.last_episode.air_date;
          const airDate = new Date(`${airDateStr}T00:00:00`);
          
          // Include if aired in the past week (but not future episodes)
          return airDate >= oneWeekAgo && airDate < todayStart;
        })
        .map(show => {
          const airDateStr = show.last_episode.air_date;
          const airDate = new Date(`${airDateStr}T00:00:00`);
          
          // Calculate days ago
          const dayDiff = Math.round((todayStart - airDate) / (1000 * 60 * 60 * 24));
          
          return {
            ...show,
            daysAgo: dayDiff,
            episodeAirDate: airDate
          };
        })
        .sort((a, b) => b.episodeAirDate - a.episodeAirDate); // Most recent first

      setRecentEpisodes(recentlyAired);

      // Load statistics for recent shows - use tmdb_id for accurate matching
      const statsPromises = recent.map(show => {
        const statsParams = show.tmdb_id ? `?tmdb_id=${show.tmdb_id}` : '';
        return axios.get(`${API}/shows/${encodeURIComponent(show.title)}/statistics${statsParams}`, { withCredentials: true })
          .then(res => ({ id: show.id, title: show.title, tmdb_id: show.tmdb_id, stats: res.data }))
          .catch(err => {
            console.error(`Error loading stats for ${show.title}:`, err);
            return { id: show.id, title: show.title, tmdb_id: show.tmdb_id, stats: null };
          });
      });

      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(result => {
        if (result.stats) {
          // Use show id as key for accurate mapping (handles same-named shows)
          statsMap[result.id] = result.stats;
        }
      });
      setShowStatistics(statsMap);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const handleShowAdded = () => {
    loadDashboardData();
  };

  return (
    <>
      <SEO 
        title={`${user.name}'s Dashboard | baad.tv`}
        description={`View your TV show tracking dashboard. You're watching ${stats.watching} shows, waiting for ${stats.waiting}, completed ${stats.completed}, and have ${stats.wantToWatch} on your watchlist.`}
        url="/dashboard"
      />
      <div className={dashboardStyles.dashboardContainer}>
        <Navigation user={user} setUser={setUser} currentPage="dashboard" />
      
      <div className={dashboardStyles.dashboardContent}>
        <ShowFinderBanner />
        <div className={dashboardStyles.dashboardHeader}>
          <div>
            <h1 data-testid="dashboard-title">Welcome back, {user.name.split(' ')[0]}!</h1>
            <p>What's in your paddock</p>
          </div>
          <Button onClick={() => setShowShareModal(true)} data-testid="invite-friends-btn">
            <UserPlus size={20} />
            Invite Friends
          </Button>
        </div>

        <div className={dashboardStyles.statsGrid}>
          <Card 
            className={`${dashboardStyles.statCard} ${dashboardStyles.clickable}`}
            data-testid="stat-watching"
            onClick={() => navigate('/my-shows?status=watching')}
          >
            <CardContent className={dashboardStyles.statContent}>
              <div className={`${dashboardStyles.statIcon} ${dashboardStyles.watching}`}>
                <Eye size={24} />
              </div>
              <div className={dashboardStyles.statInfo}>
                <div className={dashboardStyles.statNumber}>{stats.watching}</div>
                <div className={dashboardStyles.statLabel}>Currently Watching</div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`${dashboardStyles.statCard} ${dashboardStyles.clickable}`}
            data-testid="stat-waiting"
            onClick={() => navigate('/my-shows?status=waiting')}
          >
            <CardContent className={dashboardStyles.statContent}>
              <div className={`${dashboardStyles.statIcon} ${dashboardStyles.waiting}`}>
                <Clock size={24} />
              </div>
              <div className={dashboardStyles.statInfo}>
                <div className={dashboardStyles.statNumber}>{stats.waiting}</div>
                <div className={dashboardStyles.statLabel}>Waiting for Next Season</div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`${dashboardStyles.statCard} ${dashboardStyles.clickable}`}
            data-testid="stat-completed"
            onClick={() => navigate('/my-shows?status=completed')}
          >
            <CardContent className={dashboardStyles.statContent}>
              <div className={`${dashboardStyles.statIcon} ${dashboardStyles.completed}`}>
                <Check size={24} />
              </div>
              <div className={dashboardStyles.statInfo}>
                <div className={dashboardStyles.statNumber}>{stats.completed}</div>
                <div className={dashboardStyles.statLabel}>Completed</div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`${dashboardStyles.statCard} ${dashboardStyles.clickable}`}
            data-testid="stat-want"
            onClick={() => navigate('/my-shows?status=want')}
          >
            <CardContent className={dashboardStyles.statContent}>
              <div className={`${dashboardStyles.statIcon} ${dashboardStyles.want}`}>
                <Bookmark size={24} />
              </div>
              <div className={dashboardStyles.statInfo}>
                <div className={dashboardStyles.statNumber}>{stats.wantToWatch}</div>
                <div className={dashboardStyles.statLabel}>Want to Watch</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coming Episodes Section */}
        {comingEpisodes.length > 0 && (
          <Card className={dashboardStyles.comingEpisodesCard}>
            <CardHeader>
              <CardTitle className={dashboardStyles.comingEpisodesTitle}>
                <Calendar size={20} />
                Coming Episodes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={dashboardStyles.episodesList}>
                {comingEpisodes.map(show => (
                  <div 
                    key={show.id} 
                    className={dashboardStyles.episodeItem}
                    onClick={() => handleShowClick(show)}
                  >
                    <div className={dashboardStyles.episodePoster}>
                      {show.image_url ? (
                        <img src={show.image_url} alt={show.title} />
                      ) : (
                        <div className={dashboardStyles.noPoster}>
                          <Tv size={24} />
                        </div>
                      )}
                    </div>
                    <div className={dashboardStyles.episodeInfo}>
                      <div className={dashboardStyles.episodeShowTitle}>{show.title}</div>
                      <div className={dashboardStyles.episodeDetails}>
                        <span className={dashboardStyles.episodeNumber}>
                          S{String(show.next_episode.season_number).padStart(2, '0')}E{String(show.next_episode.episode_number).padStart(2, '0')}
                        </span>
                        {show.next_episode.name && (
                          <span className={dashboardStyles.episodeName}>"{show.next_episode.name}"</span>
                        )}
                      </div>
                      <div className={dashboardStyles.episodeAirDate}>
                        {show.localReleaseTime.toLocaleDateString('en-NZ', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                    <div className={dashboardStyles.episodeCountdown}>
                      {show.daysUntil === 0 ? (
                        <span className={dashboardStyles.countdownToday}>Today!</span>
                      ) : show.daysUntil === 1 ? (
                        <span className={dashboardStyles.countdownSoon}>Tomorrow</span>
                      ) : (
                        <span className={dashboardStyles.countdownDays}>{show.daysUntil} days</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Episodes Section (Last Week) */}
        {recentEpisodes.length > 0 && (
          <Card className={dashboardStyles.comingEpisodesCard}>
            <CardHeader>
              <CardTitle className={dashboardStyles.comingEpisodesTitle}>
                <History size={20} />
                Last Week's Episodes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={dashboardStyles.episodesList}>
                {recentEpisodes.map(show => (
                  <div 
                    key={`recent-${show.id}`} 
                    className={dashboardStyles.episodeItem}
                    onClick={() => handleShowClick(show)}
                  >
                    <div className={dashboardStyles.episodePoster}>
                      {show.image_url ? (
                        <img src={show.image_url} alt={show.title} />
                      ) : (
                        <div className={dashboardStyles.noPoster}>
                          <Tv size={24} />
                        </div>
                      )}
                    </div>
                    <div className={dashboardStyles.episodeInfo}>
                      <div className={dashboardStyles.episodeShowTitle}>{show.title}</div>
                      <div className={dashboardStyles.episodeDetails}>
                        <span className={dashboardStyles.episodeNumber}>
                          S{String(show.last_episode.season_number).padStart(2, '0')}E{String(show.last_episode.episode_number).padStart(2, '0')}
                        </span>
                        {show.last_episode.name && (
                          <span className={dashboardStyles.episodeName}>"{show.last_episode.name}"</span>
                        )}
                      </div>
                      <div className={dashboardStyles.episodeAirDate}>
                        {show.episodeAirDate.toLocaleDateString('en-NZ', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                    <div className={dashboardStyles.episodeCountdown}>
                      {show.daysAgo === 1 ? (
                        <span className={dashboardStyles.countdownRecent}>Yesterday</span>
                      ) : (
                        <span className={dashboardStyles.countdownDaysAgo}>{show.daysAgo} days ago</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

        )}

        {/* Trending promo — same rich CTA shown on the public landing page,
            with logged-in copy + the embedded pick rotating through the
            current trending list every 5 seconds. */}
        <div className={dashboardStyles.trendingPickWrap} data-testid="dashboard-trending-pick">
          <TrendingPromoSection
            user={user}
            compact
            rotatePick
            eyebrow="Fresh from the flock"
            body="Pop over to our trending page and you'll find the shows everyone is talking about this week — pick your patch of the world and we'll show you what's hot, where to stream it, and whether Baarry's flock has given it a thumbs up."
          />
        </div>

        <Card className={dashboardStyles.recentShowsCard}>
          <CardHeader>
            <CardTitle>Recent Shows</CardTitle>
          </CardHeader>
          <CardContent>
            {recentShows.length === 0 ? (
              <div className={dashboardStyles.emptyState} data-testid="empty-shows">
                <p>No shows yet. Start tracking your favorites!</p>
                <Button onClick={() => setShowModal(true)} className="mt-4">
                  Add Your First Show
                </Button>
              </div>
            ) : (
              <div className={showStyles.showsGrid}>
                {recentShows.map(show => (
                  <Card 
                    key={show.id} 
                    className={`${showStyles.showCard} ${showStyles.clickableCard}`}
                    data-testid={`show-${show.id}`}
                    onClick={() => handleShowClick(show)}
                  >
                    <CardContent className={showStyles.showCardContent}>
                      {(show.backdrop_url || show.image_url) && (
                        <div className={showStyles.showImage}>
                          <img 
                            src={show.backdrop_url || show.image_url} 
                            alt={`${show.title} TV show poster`}
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className={showStyles.showHeader}>
                        <h3>{show.title}</h3>
                        <span className={showStyles.statusBadge}>{show.status}</span>
                      </div>

                      {(show.release_date || (show.genres && show.genres.length > 0)) && (
                        <div className={showStyles.showMetadataCompact}>
                          {show.release_date && (
                            <span className={showStyles.metadataYear}>{show.release_date.substring(0, 4)}</span>
                          )}
                          {show.genres && show.genres.length > 0 && (
                            <>
                              {show.release_date && <span className={showStyles.metadataSeparator}>•</span>}
                              <span className={showStyles.metadataGenres}>{show.genres.slice(0, 2).join(', ')}</span>
                            </>
                          )}
                        </div>
                      )}

                      {show.overview && (
                        <p className={showStyles.showOverview}>{show.overview}</p>
                      )}

                      <div className={showStyles.showDetails}>
                        {(show.streaming_service || show.custom_service) && (
                          <div className={showStyles.detailRow}>
                            <span className={showStyles.detailLabel}>Streaming on:</span>
                            <span className={showStyles.detailValue}>
                              <ServiceLabel name={show.streaming_service || show.custom_service} />
                            </span>
                          </div>
                        )}

                        {show.personal_rating && (
                          <div className={showStyles.detailRow}>
                            <span className={showStyles.detailLabel}>Your Rating:</span>
                            <span className={`${showStyles.detailValue} ${showStyles.rating}`}>
                              ⭐ {show.personal_rating}/10
                            </span>
                          </div>
                        )}

                        {showStatistics[show.id] && (
                          <div className={showStyles.statisticsSection}>
                            {showStatistics[show.id].all_users.count === 0 ? (
                              <div className={showStyles.firstReviewMessage}>
                                No one has reviewed this yet
                              </div>
                            ) : showStatistics[show.id].all_users.count === 1 && showStatistics[show.id].current_user_reviewed ? (
                              <div className={showStyles.firstReviewMessage}>
                                ⭐ You are the first to Review
                              </div>
                            ) : (
                              <>
                                {showStatistics[show.id].friends.average && (
                                  <div className={showStyles.statRow}>
                                    <span className={showStyles.statLabel}>👥 Friends' Average:</span>
                                    <span className={showStyles.statValue}>
                                      ⭐ {showStatistics[show.id].friends.average}/10
                                      <span className={showStyles.statCount}>({showStatistics[show.id].friends.count})</span>
                                    </span>
                                  </div>
                                )}
                                {showStatistics[show.id].all_users.average && (
                                  <div className={showStyles.statRow}>
                                    <span className={showStyles.statLabel}>🌎 All Users' Average:</span>
                                    <span className={showStyles.statValue}>
                                      ⭐ {showStatistics[show.id].all_users.average}/10
                                      <span className={showStyles.statCount}>({showStatistics[show.id].all_users.count})</span>
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {show.personal_review && (
                          <div className={showStyles.reviewSection}>
                            <span className={showStyles.detailLabel}>Your Review:</span>
                            <p className={showStyles.reviewText}>{show.personal_review}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showModal && (
        <ShowModal
          mode="add"
          user={user}
          onClose={() => setShowModal(false)}
          onSuccess={handleShowAdded}
        />
      )}

      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />

      {selectedShowForReviews && (
        <ShowReviewsModal
          show={selectedShowForReviews}
          onClose={() => setSelectedShowForReviews(null)}
        />
      )}
      </div>
    </>
  );
};

export default Dashboard;